import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createWorkOrderAssignmentExpense } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderExpenseMutations";
import {
  Currency,
  ExpensePaidBy,
  ExpensePaidTo,
  ExpenseType,
  TaxRegistration,
} from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { toast } from "sonner";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { useProDriver } from "@/app/ProDrivers/Drivers/Hooks/useProDriver";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";
import { addTax, getTaxRate } from "@/app/Payments/Utils/taxRates";
import { useFormikContext } from "formik";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

const driverPayValidation = z.object({
  amount: z.number().min(1, "Amount is required"),
});

type DriverPayFormValues = z.infer<typeof driverPayValidation>;

export function AddDriverPayDialog({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(createWorkOrderAssignmentExpense);
  const assignment = useWorkOrderAssignment(workOrderAssignmentId);
  const { tax_registration } = useProDriver(assignment.pro_driver_id);

  const defaultCurrency = assignment.workOrder.currency;

  return (
    <BaseDialog title="Add Driver Pay">
      <GenericForm<DriverPayFormValues>
        initialValues={{
          amount: null!,
        }}
        validationSchema={toFormikValidationSchema(driverPayValidation)}
        onSubmit={async (values) => {
          await mutateAsync({
            input: {
              workOrderAssignment: { connect: workOrderAssignmentId },
              type: ExpenseType.DriverPay,
              paid_by: ExpensePaidBy.Imoova,
              paid_to: ExpensePaidTo.Driver,
              amount: addTax(values.amount, tax_registration),
              currency: defaultCurrency,
            },
          });

          toast.success("Driver pay recorded successfully");
          await close();
        }}
      >
        <DriverPayFormFields
          taxRegistration={tax_registration}
          currency={defaultCurrency}
        />
        <DialogSubmitButton text="Add Driver Pay" />
      </GenericForm>
    </BaseDialog>
  );
}

function DriverPayFormFields({
  taxRegistration,
  currency,
}: {
  taxRegistration: TaxRegistration;
  currency: Currency;
}) {
  const taxRate = getTaxRate(taxRegistration);
  const hasTax = taxRate > 0;

  const taxNotice = hasTax
    ? `This driver is registered for ${taxRegistration.replace("_", " ")}. Tax will be added.`
    : "This driver is not registered for tax.";

  return (
    <>
      <div className="col-span-full">
        <InfoBanner>{taxNotice}</InfoBanner>
      </div>
      <CurrencyInput
        name="amount"
        label={hasTax ? "Amount (exc. tax)" : "Amount"}
        currency={currency}
        autoFocus
      />
      {hasTax ? <TaxPreview taxRate={taxRate} currency={currency} /> : null}
    </>
  );
}

function TaxPreview({
  taxRate,
  currency,
}: {
  taxRate: number;
  currency: Currency;
}) {
  const { values } = useFormikContext<DriverPayFormValues>();
  const amount = values.amount ?? 0;
  const tax = Math.round(amount * taxRate);
  const incTax = amount + tax;

  return (
    <p className="col-span-full text-sm text-gray-500">
      Tax: {formatCurrency(tax, currency)} · Total inc. tax:{" "}
      {formatCurrency(incTax, currency)}
    </p>
  );
}
